import {
  PDF_SIGNATORY_TYPE_STATIC,
} from "@/utils/pdf_report_signature_helpers";

const build_local_id = (prefix) => `${prefix}_${Date.now()}_${Math.random()}`;
const DEFAULT_CHECKLIST_FIELDS = ["evaluation_result"];
const build_timestamp_part = () => {
  const date = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  const milliseconds = String(date.getMilliseconds()).padStart(3, "0");
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
    pad(date.getHours()),
    pad(date.getMinutes()),
    pad(date.getSeconds()),
    milliseconds,
  ].join("");
};

const build_title_case = (value) => {
  const text = String(value || "");
  const words = text.split("_");
  const label = words
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");
  return label;
};

export const build_empty_binding = () => ({
  source_type: "system_field",
  system_field: "entity_name",
  section_question_id: "",
  question_label: "",
  value: "",
});

export const build_field_item = () => ({
  local_id: build_local_id("field_item"),
  label: "",
  binding: build_empty_binding(),
});

export const build_checklist_row = () => ({
  local_id: build_local_id("checklist_row"),
  label: "",
  binding: {
    source_type: "section_question",
    system_field: "",
    section_question_id: "",
    value: "",
  },
});

export const build_table_column = () => ({
  local_id: build_local_id("table_column"),
  label: "",
  binding: build_empty_binding(),
});

export const build_table_cell = () => ({
  local_id: build_local_id("table_cell"),
  binding: build_empty_binding(),
});

export const build_signatory = () => ({
  local_id: build_local_id("signatory"),
  signatory_type: PDF_SIGNATORY_TYPE_STATIC,
  role: "",
  name: "",
});

const build_title_config = (config) => ({
  title: String(config?.title || ""),
  subtitle: String(config?.subtitle || ""),
});

const build_rich_text_config = (config) => ({
  body: String(config?.body || ""),
});

const build_binding = (binding) => ({
  source_type: String(binding?.source_type || "system_field"),
  system_field: String(binding?.system_field || ""),
  section_question_id: String(binding?.section_question_id || ""),
  question_label: String(binding?.question_label || ""),
  value: String(binding?.value || ""),
});

const build_field_list_config = (config) => {
  const items = Array.isArray(config?.items) ? config.items : [];
  const normalized_items = items.map((item) => ({
    local_id: build_local_id("field_item"),
    label: String(item?.label || ""),
    binding: build_binding(item?.binding),
  }));
  return {
    items: normalized_items.length ? normalized_items : [build_field_item()],
  };
};

const build_checklist_config = (config) => {
  const rows = Array.isArray(config?.rows) ? config.rows : [];
  const normalized_rows = rows.map((row) => ({
    local_id: build_local_id("checklist_row"),
    label: String(row?.label || ""),
    binding: build_binding(row?.binding),
  }));
  return {
    display_fields: Array.isArray(config?.display_fields) && config.display_fields.length
      ? Array.from(new Set(config.display_fields.map((field_name) => String(field_name || ""))))
      : [...DEFAULT_CHECKLIST_FIELDS],
    rows: normalized_rows.length ? normalized_rows : [build_checklist_row()],
  };
};

const build_static_rows = (rows, column_count) => {
  const normalized_rows = Array.isArray(rows) ? rows : [];
  const next_rows = normalized_rows.map((row) => {
    const cells = Array.isArray(row) ? row : [];
    return cells.map((cell) => ({
      local_id: build_local_id("table_cell"),
      binding: build_binding(cell?.binding),
    }));
  });
  if (next_rows.length) {
    return next_rows;
  }
  return [[...Array(column_count)].map(() => build_table_cell())];
};

const build_data_table_config = (config) => {
  const columns = Array.isArray(config?.columns) ? config.columns : [];
  const normalized_columns = columns.map((column) => ({
    local_id: build_local_id("table_column"),
    label: String(column?.label || ""),
    binding: build_binding(column?.binding),
  }));
  const next_columns = normalized_columns.length
    ? normalized_columns
    : [build_table_column()];
  return {
    row_source: {
      source_type: String(config?.row_source?.source_type || "static_rows"),
      question_ids: Array.isArray(config?.row_source?.question_ids)
        ? config.row_source.question_ids.map((question_id) => String(question_id))
        : [],
    },
    columns: next_columns,
    rows: build_static_rows(config?.rows, next_columns.length),
  };
};

const build_sign_off_config = (config) => {
  const signatories = Array.isArray(config?.signatories) ? config.signatories : [];
  const normalized_signatories = signatories.map((signatory) => ({
    local_id: build_local_id("signatory"),
    signatory_type: String(
      signatory?.signatory_type || PDF_SIGNATORY_TYPE_STATIC
    ),
    role: String(signatory?.role || ""),
    name: String(signatory?.name || ""),
  }));
  return {
    signatories: normalized_signatories.length
      ? normalized_signatories
      : [build_signatory()],
  };
};

const build_section_config = (section_type, config) => {
  if (section_type === "document_title") {
    return build_title_config(config);
  }
  if (section_type === "rich_text") {
    return build_rich_text_config(config);
  }
  if (section_type === "field_list") {
    return build_field_list_config(config);
  }
  if (section_type === "checklist_matrix") {
    return build_checklist_config(config);
  }
  if (section_type === "data_table") {
    return build_data_table_config(config);
  }
  return build_sign_off_config(config);
};

const get_default_section_title = (section_type, order_index) => {
  if (section_type === "document_title") {
    return "Document title";
  }
  if (section_type === "rich_text") {
    return "Narrative";
  }
  if (section_type === "field_list") {
    return "Field list";
  }
  if (section_type === "checklist_matrix") {
    return "Checklist";
  }
  if (section_type === "data_table") {
    return "Data table";
  }
  if (section_type === "sign_off") {
    return "Sign off";
  }
  return `Section ${order_index}`;
};

export const build_section_form = (section_type, order_index, section = null) => ({
  local_id: build_local_id("section"),
  id: section?.id || null,
  section_type,
  title: String(section?.title || get_default_section_title(section_type, order_index)),
  order_index,
  is_visible: section?.is_visible !== false,
  config: build_section_config(section_type, section?.config),
});

export const build_template_form = (initial_template, project_id) => {
  const sections = Array.isArray(initial_template?.sections)
    ? initial_template.sections.map((section, index) =>
        build_section_form(section.section_type, index + 1, section)
      )
    : [];
    const template_config = {
      template_id: initial_template?.id || null,
      project_id,
      save_as_template:
        initial_template?.save_as_template !== false || Boolean(initial_template?.id),
      name: String(initial_template?.name || ""),
      description: String(initial_template?.description || ""),
      generation_mode: String(initial_template?.generation_mode || "manual"),
      trigger_status_id: initial_template?.trigger_status_id || "",
      logo_url: String(initial_template?.logo_url || ""),
      header_title: String(initial_template?.header_title || ""),
      header_subtitle: String(initial_template?.header_subtitle || ""),
      header_text: String(initial_template?.header_text || ""),
      footer_text: String(initial_template?.footer_text || ""),
      is_active: initial_template?.is_active !== false,
      auto_add_to_vault: initial_template?.auto_add_to_vault !== false,
      entity_type_ids: Array.isArray(initial_template?.entity_type_assignments)
        ? initial_template.entity_type_assignments
            .map((assignment) => assignment.entity_type?.id)
            .filter(Boolean)
        : [],
      sections,
    };
    return template_config;
};

const build_binding_payload = (binding) => ({
  source_type: String(binding?.source_type || ""),
  system_field: String(binding?.system_field || ""),
  section_question_id: Number(binding?.section_question_id || 0) || null,
  value: String(binding?.value || ""),
});

const build_payload_label = (label, binding) => {
  const trimmed_label = String(label || "").trim();
  if (trimmed_label) {
    return trimmed_label;
  }
  if (String(binding?.question_label || "").trim()) {
    return String(binding.question_label).trim();
  }
  return build_default_label_from_binding(binding);
};

const build_field_list_payload = (section) => ({
  items: (section.config.items || []).map((item) => ({
    label: build_payload_label(item.label, item.binding),
    binding: build_binding_payload(item.binding),
  })),
});

const build_checklist_payload = (section) => ({
  display_fields: (section.config.display_fields || []).length
    ? section.config.display_fields
    : [...DEFAULT_CHECKLIST_FIELDS],
  rows: (section.config.rows || []).map((row) => ({
    label: build_payload_label(row.label, row.binding),
    binding: build_binding_payload(row.binding),
  })),
});

const build_table_payload = (section) => ({
  row_source: {
    source_type: String(section.config.row_source?.source_type || "static_rows"),
    question_ids: (section.config.row_source?.question_ids || []).map((question_id) =>
      Number(question_id || 0)
    ).filter(Boolean),
  },
  columns: (section.config.columns || []).map((column) => ({
    label: build_payload_label(column.label, column.binding),
    binding: build_binding_payload(column.binding),
  })),
  rows: (section.config.rows || []).map((row) =>
    row.map((cell) => ({
      binding: build_binding_payload(cell.binding),
    }))
  ),
});

const build_sign_off_payload = (section) => ({
  signatories: (section.config.signatories || []).map((signatory) => ({
    signatory_type: String(
      signatory.signatory_type || PDF_SIGNATORY_TYPE_STATIC
    ),
    role: String(signatory.role || ""),
    name: String(signatory.name || ""),
  })),
});

const build_section_config_payload = (section) => {
  if (section.section_type === "document_title") {
    return {
      title: String(section.config.title || ""),
      subtitle: String(section.config.subtitle || ""),
    };
  }
  if (section.section_type === "rich_text") {
    return { body: String(section.config.body || "") };
  }
  if (section.section_type === "field_list") {
    return build_field_list_payload(section);
  }
  if (section.section_type === "checklist_matrix") {
    return build_checklist_payload(section);
  }
  if (section.section_type === "data_table") {
    return build_table_payload(section);
  }
  return build_sign_off_payload(section);
};

export const build_template_payload = (template_form) => {
  const template_name = String(template_form.name || "").trim();
  const generated_name = `one-off-pdf-${build_timestamp_part()}`;
  const payload = {
    project_id: Number(template_form.project_id || 0),
    name:
      template_form.save_as_template === false
        ? generated_name
        : template_name,
    description: String(template_form.description || "").trim(),
    generation_mode: String(template_form.generation_mode || "manual"),
    trigger_status_id: Number(template_form.trigger_status_id || 0) || null,
    logo_url: String(template_form.logo_url || "").trim(),
    header_title: String(template_form.header_title || "").trim(),
    header_subtitle: String(template_form.header_subtitle || "").trim(),
    header_text: String(template_form.header_text || "").trim(),
    footer_text: String(template_form.footer_text || "").trim(),
    theme_config: {},
    page_config: {},
    is_active: Boolean(template_form.is_active),
    auto_add_to_vault: Boolean(template_form.auto_add_to_vault),
    entity_type_ids: (template_form.entity_type_ids || []).map((entity_type_id) =>
      Number(entity_type_id || 0)
    ).filter(Boolean),
    sections: (template_form.sections || []).map((section, index) => ({
      section_type: section.section_type,
      title: String(section.title || "").trim(),
      order_index: index + 1,
      is_visible: Boolean(section.is_visible),
      config: build_section_config_payload(section),
    })),
  };
  if (template_form.template_id) {
    payload.template_id = Number(template_form.template_id);
  }
  return payload;
};

export const build_pdf_report_config_payload = (template_form) => ({
  output_format: "pdf",
  entity_type_ids: (template_form.entity_type_ids || []).map((entity_type_id) =>
    Number(entity_type_id || 0)
  ).filter(Boolean),
  generation_mode: String(template_form.generation_mode || "manual"),
  trigger_status_id: Number(template_form.trigger_status_id || 0) || null,
  logo_url: String(template_form.logo_url || "").trim(),
  header_title: String(template_form.header_title || "").trim(),
  header_subtitle: String(template_form.header_subtitle || "").trim(),
  header_text: String(template_form.header_text || "").trim(),
  footer_text: String(template_form.footer_text || "").trim(),
  theme_config: {},
  page_config: {},
  auto_add_to_vault: Boolean(template_form.auto_add_to_vault),
  sections: (template_form.sections || []).map((section, index) => ({
    section_type: section.section_type,
    title: String(section.title || "").trim(),
    order_index: index + 1,
    is_visible: Boolean(section.is_visible),
    config: build_section_config_payload(section),
  })),
});

export const build_formal_document_config_payload = build_pdf_report_config_payload;

export const build_question_option = (row) => {
  const option = {
    id: String(
      row?.section_question_id || row?.id || row?.section_question?.id || ""
    ),
    question_text: String(
      row?.question_text || row?.question?.question_text || "Question"
    ),
    question_type: build_title_case(
      row?.question_type || row?.question?.question_type || ""
    ),
    form_name: String(row?.form_name || row?.form?.form_name || ""),
    section_name: String(row?.section_name || row?.section?.name || ""),
  };
  return option;
};

export const build_question_option_label = (question_option) => {
  const parts = [question_option.question_text];
  if (question_option.form_name) {
    parts.push(question_option.form_name);
  }
  if (question_option.section_name) {
    parts.push(question_option.section_name);
  }
  const label = parts.join(" | ");
  return label;
};

export const find_question_option = (question_options, section_question_id) => {
  const normalized_id = String(section_question_id || "");
  const question_option = question_options.find(
    (item) => String(item.id) === normalized_id
  );
  return question_option || null;
};

export const build_system_field_label = (system_field) => build_title_case(system_field);

export const build_default_label_from_binding = (binding) => {
  const source_type = String(binding?.source_type || "");
  const system_field = String(binding?.system_field || "");
  const static_value = String(binding?.value || "").trim();
  if (source_type === "system_field") {
    return build_system_field_label(system_field || "field");
  }
  if (source_type === "static_text") {
    return static_value || "Field";
  }
  return "Question";
};

export const sync_binding_source = (binding) => {
  const source_type = String(binding?.source_type || "");
  if (source_type === "static_text") {
    binding.system_field = "";
    binding.section_question_id = "";
    binding.question_label = "";
    return;
  }
  if (source_type === "system_field") {
    binding.section_question_id = "";
    binding.question_label = "";
    binding.value = "";
    if (!binding.system_field) {
      binding.system_field = "entity_name";
    }
    return;
  }
  binding.system_field = "";
  binding.value = "";
};

export const apply_default_label_to_target = (target_row, question_options) => {
  const current_label = String(target_row?.label || "").trim();
  if (current_label) {
    return;
  }
  const binding = target_row?.binding || {};
  if (binding.source_type === "system_field") {
    target_row.label = build_default_label_from_binding(binding);
    return;
  }
  if (binding.source_type !== "section_question") {
    target_row.label = build_default_label_from_binding(binding);
    return;
  }
  const question_option = find_question_option(
    question_options,
    binding.section_question_id
  );
  target_row.label =
    question_option?.question_text || build_default_label_from_binding(binding);
};

export const build_placeholder_label_from_binding = (binding, question_options) => {
  if (binding?.source_type !== "section_question") {
    return build_default_label_from_binding(binding);
  }
  const question_option = find_question_option(
    question_options,
    binding?.section_question_id
  );
  return question_option?.question_text || String(binding?.question_label || "").trim() || "Question";
};
